// LibrarySection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './LibrarySection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import TermButton from './termbutton/TermButton';
import Checkbox from 'components/checkbox/Checkbox';
import TextInput from 'components/textinput/TextInput';

const FIELD_TYPE_ICON_KEYS = {
  activity: 'timeline',
  barcode: 'barcode',
  boolean: 'toggle_on',
  checklist: 'checklist',
  comments: 'chat',
  cover: 'image',
  money: 'attach_money',
  currency: 'payments',
  date: 'calendar_today',
  daterange: 'date_range',
  datetime: 'schedule',
  datetimerange: 'event_repeat',
  subtitle: 'short_text',
  enum: 'list_alt',
  location: 'location_on',
  number: 'tag',
  relation: 'format_list_bulleted',
  percentage: 'percent',
  people: 'group',
  richtext: 'article',
  signature: 'draw',
  string: 'title',
  tags: 'sell',
  text: 'short_text',
  url: 'link',
};

const getFieldType = (field) => String(field?.type || '').trim().toLowerCase();

/**
 * getFieldLabel
 * 
 * Extracts a user-friendly label for a given field, prioritizing the 'label' field, then 'name', and
 * falling back to 'Untitled Field' if neither is available or non-empty.
 * 
 * @param {object} field - The field object to extract the label from.
 * 
 * @returns {string} A string representing the field's label for display purposes.
 */
const getFieldLabel = (field) => {
  const label = String(field?.label || field?.name || '').trim();
  return label || 'Untitled Field';
};

/**
 * getFieldIconKey
 * 
 * Determines the appropriate icon key for a given field based on its type or explicit iconKey.
 * 
 * @param {object} field - The field object to determine the icon for.
 * 
 * @returns {string} The icon key corresponding to the field's type or explicit iconKey.
 */
const getFieldIconKey = (field) => {
  const explicitIcon = String(field?.iconKey || '').trim();
  if (explicitIcon) return explicitIcon;

  const type = getFieldType(field);
  return FIELD_TYPE_ICON_KEYS[type] || 'category';
};

const getFieldDescription = (field) => String(field?.fieldDescription || '').trim();

const getFieldKey = (field) => String(field?.name || field?.key || '').trim();

/**
 * getEnumOptionsSubtitle
 * 
 * Generates a subtitle string for enum fields listing their options.
 * 
 * @param {object} field - The field object to extract enum options from.
 * 
 * @return {string} A comma-separated list of enum option labels, or an empty string if not applicable.
 */
const getEnumOptionsSubtitle = (field) => {
  if (getFieldType(field) !== 'enum') return '';

  const options = Array.isArray(field?.options) ? field.options : [];
  const values = options
    .map((option) => {
      if (typeof option === 'string') return option.trim();
      if (!option || typeof option !== 'object') return '';

      const enTranslation = String(option?.translations?.en?.label || '').trim();
      if (enTranslation) return enTranslation;

      const label = String(option.label || option.value || option.name || '').trim();
      return label;
    })
    .filter(Boolean);

  return values.length ? values.join(', ') : '';
};

/**
 * LibrarySection
 *
 * Renders library term and addon selection UI for provisioning.
 *
 * @param {string} query - Current library search query.
 * @param {function} setQuery - Updates the library search query.
 * @param {boolean} loading - Whether library terms are loading.
 * @param {string} error - Optional library error message.
 * @param {array} terms - Library terms available for this base.
 * @param {object|null} selectedTerm - Selected term when viewing addons.
 * @param {object|null} selectedAddon - Selected addon for review before provisioning.
 * @param {boolean} isAddonStep - Whether the addons list is shown.
 * @param {boolean} isAddonPreviewStep - Whether the addon review step is shown.
 * @param {boolean} isProvisioning - Whether provisioning is in progress.
 * @param {string} provisioningAddonKey - Key of the addon being provisioned.
 * @param {array} selectedFieldKeys - Field keys currently selected for provisioning.
 * @param {string} baseKey - Selected base key for library access.
 * @param {function} onSelectTerm - Called when a term is selected.
 * @param {function} onSelectAddon - Called when a addon is selected.
 * @param {function} onToggleSelectedField - Toggles a selected field key.
 *
 * @returns {ReactNode} The rendered component.
 */
const LibrarySection = React.memo(({
  query,
  setQuery,
  loading,
  error,
  terms,
  selectedTerm,
  selectedAddon,
  isAddonStep,
  isAddonPreviewStep,
  isProvisioning,
  provisioningAddonKey,
  selectedFieldKeys,
  baseKey,
  onSelectTerm,
  onSelectAddon,
  onToggleSelectedField,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- UTILITIES ----- //

  const renderTermRows = () => {
    if (loading) {
      return (
        <div
          className="library-section-empty"
        >
          Loading database templates...
        </div>
      );
    }

    if (!baseKey) {
      return (
        <div
          className="library-section-empty"
        >
          Select a base to use database templates.
        </div>
      );
    }

    if (!terms.length) {
      return (
        <div
          className="library-section-empty"
        >
          No database templates available for this base.
        </div>
      );
    }

    return terms.map((term) => {
      const addonCount = Array.isArray(term.addons) ? term.addons.length : 0;
      const termLabel = String(term?.labelPlural || term?.pluralLabel || '').trim() || term.label;
      return (
        <TermButton
          key={term.key}
          label={termLabel}
          addonCount={addonCount}
          isProvisioning={isProvisioning}
          onClick={() => onSelectTerm(term)}
        />
      );
    });
  };

  /**
   * renderAddonRows
   * 
   * Renders the list of addons for the selected term, sorted by selection count and title, and includes
   * provisioning state handling.
   * 
   * @returns {ReactNode} A list of addon buttons or an appropriate message if no addons are available.
   */
  const renderAddonRows = () => {
    const addons = [...(selectedTerm?.addons || [])]
      .sort((left, right) => {
        const leftCount = Number(left?.selectionCount || 0);
        const rightCount = Number(right?.selectionCount || 0);
        if (rightCount !== leftCount) return rightCount - leftCount;

        const leftTitle = String(left?.title || '').trim().toLowerCase();
        const rightTitle = String(right?.title || '').trim().toLowerCase();
        return leftTitle.localeCompare(rightTitle);
      });

    if (!addons.length) {
      return (
        <div
          className="library-section-empty"
        >
          No addons found for this term.
        </div>
      );
    }

    return addons.map((addon) => {
      const isAddonProvisioning = isProvisioning && provisioningAddonKey === addon.key;
      const selectionCount = Number(addon?.selectionCount || 0);
      const selectionLabel = `${selectionCount} ${selectionCount === 1 ? 'usage' : 'usages'}`;
      return (
        <button
          className="library-section-row"
          key={addon.key}
          disabled={isProvisioning}
          type="button"
          onClick={() => onSelectAddon(addon)}
        >

          {/* ROW HEADER */}
          <div className="library-section-row-header">

            {/* ROW ICON */}
            {addon.iconKey ? (

              <div className="library-section-row-icon">
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey={addon.iconKey}
                  width={16}
                />
              </div>
            ) : null}

            {/* ROW HEADER CONTENT */}
            <div className="library-section-row-header-content">

              {/* ROW TITLE */}
              <div className="library-section-row-title">{addon.title}</div>

              {/* ROW META */}
              <div
                className="library-section-row-meta"
              >
                {(addon.schema?.fields || []).length} fields • {selectionLabel}
              </div>
            </div>
          </div>

          {/* ROW DESCRIPTION */}
          {addon.description ? (
            <div
              className="library-section-row-description"
            >
              {addon.description}
            </div>
          ) : null}

          {/* ROW META */}
          {isAddonProvisioning ? (
            <div
              className="library-section-row-meta"
            >
              Provisioning...
            </div>
          ) : null}
        </button>
      );
    });
  };

  const renderAddonPreview = () => {
    if (!selectedAddon) {
      return (
        <div
          className="library-section-empty"
        >
          Select a addon to review fields.
        </div>
      );
    }

    const selectedFieldKeySet = new Set(Array.isArray(selectedFieldKeys) ? selectedFieldKeys : []);
    const fields = [...(selectedAddon?.schema?.fields || [])]
      .map((field, index) => ({
        field,
        index,
        fieldKey: getFieldKey(field),
        selected: selectedFieldKeySet.has(getFieldKey(field)),
      }))
      .sort((left, right) => {
        if (left.selected !== right.selected) {
          return left.selected ? -1 : 1;
        }

        return left.index - right.index;
      });
    const selectedCount = fields.filter((entry) => entry.selected).length;

    return (

      <div className="library-section-preview">
        {/* PREVIEW SUMMARY */}
        <div className="library-section-preview-summary">

          {/* PREVIEW TITLE */}
          <div
            className="library-section-preview-title"
          >
            {selectedAddon.title || 'Addon'} - Default Fields
          </div>

          {/* PREVIEW DESCRIPTION */}
          {selectedAddon.description ? (
            <div
              className="library-section-preview-description"
            >
              {selectedAddon.description}
            </div>
          ) : null}

          {/* PREVIEW META */}
          <div
            className="library-section-preview-meta"
          >
            {selectedCount} of {fields.length} fields selected for provisioning
          </div>
        </div>

        {/* PREVIEW FIELDS */}
        <div className="library-section-preview-fields">

          {/* EMPTY */}
          {!fields.length ? (
            <div
              className="library-section-empty"
            >
              This addon has no fields.
            </div>
          ) : null}

          {fields.map(({ field, fieldKey, selected, index }) => {
            const name = getFieldLabel(field);
            const fieldDescription = getFieldDescription(field);
            const enumOptionsSubtitle = getEnumOptionsSubtitle(field);
            const keySeed = fieldKey || String(field?.label || '').trim();

            return (
              <div
                className={`library-section-field-row ${selected ? 'selected' : 'unselected'}`}
                key={`${keySeed || 'field'}-${index}`}
              >

                {/* FIELD CHECKBOX */}
                <div className="library-section-field-checkbox">
                  <Checkbox
                    value={selected}
                    size={18}
                    onChange={(event) => {
                      event?.stopPropagation?.();
                      onToggleSelectedField?.(fieldKey);
                    }}
                  />
                </div>

                {/* FIELD ICON */}
                <div className="library-section-field-icon">
                  <Icon
                    color={theme.foregroundMuted}
                    height={16}
                    iconKey={getFieldIconKey(field)}
                    width={16}
                  />
                </div>

                {/* FIELD CONTENT */}
                <div className="library-section-field-content">

                  {/* FIELD TITLE */}
                  <div
                    className="library-section-field-title"
                  >
                    {name}
                  </div>
                  {/* 
                  <div

                    className="library-section-field-subtitle"
                    style={{
                      color: theme.foregroundMuted,
                    }}
                  >
                    {typeLabel}
                  </div>
                  */}

                  {/* FIELD DESCRIPTION */}
                  {fieldDescription ? (
                    <div
                      className="library-section-field-description"
                    >
                      {fieldDescription}
                    </div>
                  ) : null}

                  {/* FIELD SUBTITLE */}
                  {enumOptionsSubtitle ? (
                    <div
                      className="library-section-field-subtitle"
                    >
                      {enumOptionsSubtitle}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

            {/* PREVIEW DESCRIPTION */}
            <div
              className="library-section-preview-description"
            >
              You can add, edit or delete fields after provisioning. This is just a preview of the default 
              field setup for this new database.
            </div>

      </div>
    );
  };

  // ----- RENDER ----- //

  return (
    <div className="library-section-container">

      {/* CONTROLS */}
      <div className="library-section-controls">
        <TextInput
          autoFocus
          placeholder="Search available add-ons..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {/* ERROR */}
      {error ? (
        <div
          className="library-section-error"
          style={{
            borderColor: `${theme.warning}66`,
            color: theme.warning,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* LIST */}
      <div className="library-section-list">
        {isAddonPreviewStep ? renderAddonPreview() : null}
        {!isAddonPreviewStep && isAddonStep ? renderAddonRows() : null}
        {!isAddonPreviewStep && !isAddonStep ? renderTermRows() : null}
      </div>
    </div>
  );
});

// Dev Tools
LibrarySection.displayName = 'LibrarySection';

export default LibrarySection;
